import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  reduxForm,
  propTypes as reduxFormProps,
  getFormValues,
} from 'redux-form';
import { Container, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { useLocation, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import TimetableIcon from 'mdi-react/TimetableIcon';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import { toast } from 'react-toastify';
import validateEventsContact from './validateEventsContact';
import {
  getEventMessageTemplates,
  templateMessage,
  templateWhen,
} from '../eventMessageTemplates';
import renderField from '../../../shared/components/custom/Field';
import renderTextAreaField from '../../../shared/components/custom/TextArea';
import t, { partial } from '../../../util/translation/translation';
import Api from '../../../util/api';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import useModal from '../../../shared/hooks/useModal';
import PreviewMessages from '../../messages/components/PreviewsMessage';
import FormCard from '../../../shared/components/custom/FormCard';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';

const EventsContactForm = ({
  handleSubmit,
  initialize,
  formValues,
  eventID,
  scheduleMessageID,
  setScheduleMessageID,
  scheduleMessagesForEvent,
  getScheduleMessagesByServiceId,
  clearContactPage,
  setClearContactPage,
  startDate,
  setStartDate,
  setLoading,
  change,
  event,
}) => {
  const p = partial('EventsContactForm');
  const m = partial('MessagesForm');

  const { state } = useLocation();
  const [handleModal, visible, deleteId] = useModal();

  const initializeEdit = (message) => {
    setScheduleMessageID(message.id);
    const messageDate = message.date ? new Date(message.date) : null;
    setStartDate(messageDate);
    if (formValues) {
      initialize({
        title:
          message?.payload?.placeholders?.TITLE ||
          message?.payload?.placeholders?.SUBJECT,
        content: message?.payload?.placeholders?.BODY,
        type: message?.payload?.channels[0]?.channel,
        receiverType: message?.payload?.eventInfo?.receiverType,
        scheduleTime: messageDate,
        timeToSend: 'SCHEDULED',
      });
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const clearFields = () => {
    setClearContactPage(false);
    setScheduleMessageID(null);
    setStartDate('');
    initialize({
      title: '',
      content: '',
      type: 'push',
      receiverType: 'PARTICIPANTS',
      scheduleTime: null,
      timeToSend: 'INSTANT',
    });
  };

  const removeMessage = async (id) => {
    setLoading(true);
    try {
      await Api.events.deleteMessage(eventID, id);
      handleModal();
      getScheduleMessagesByServiceId();
    } catch (err) {
      setLoading(false);
      toast.error(err || 'errorRemovingMessage');
    }
  };

  useEffect(() => {
    clearFields();
  }, [clearContactPage]);

  useEffect(() => {
    if (state?.messageId && scheduleMessagesForEvent.length > 0) {
      const messageToShow = scheduleMessagesForEvent.find(
        (msg) => msg.id === state.messageId
      );
      initializeEdit(messageToShow);
    }
  }, [scheduleMessagesForEvent]);

  const renderSubmitButtons = () => {
    let buttonName = '';
    if (scheduleMessageID) {
      buttonName = t('ScheduledMessagesPage.updateMessage');
    } else if (formValues?.timeToSend === 'SCHEDULED') {
      buttonName = t('ScheduledMessagesPage.scheduleMessage');
    } else {
      buttonName = p('sendMessage');
    }

    return (
      <div className="messages-form__footer">
        <div className="messages-form__secondary">
          {scheduleMessageID && (
            <Button
              size="sm"
              color="secondary"
              className="mb-0"
              onClick={clearFields}
            >
              {t('shared.clear')}
            </Button>
          )}
          <Link style={{ textDecoration: 'underline' }} to="/messages?tab=2">
            {t('MessagesForm.scheduledAndHistory')}
          </Link>
        </div>
        <Button type="submit" color="primary" className="mb-0">
          {buttonName}
        </Button>
      </div>
    );
  };

  const filterPassedTime = (time) => {
    const isSameDay = moment().diff(moment(time), 'days') === 0;
    if (isSameDay) {
      return moment(time).isSameOrAfter(moment());
    }
    return true;
  };

  const isSms = formValues?.type === 'sms';

  // Ready-made messages, timed from this event's dates
  const templates = getEventMessageTemplates(event);

  const applyTemplate = (template) => {
    const date = template.sendAt.toDate();
    const message = templateMessage(template);
    change('title', message.title);
    change('content', message.body);
    change('receiverType', template.receiverType);
    change('timeToSend', 'SCHEDULED');
    change('scheduleTime', date);
    setStartDate(date);
  };

  return (
    <form onSubmit={handleSubmit} className="form d-flex messages-form">
      <Container className="flex">
        <DeleteModal
          visible={visible}
          handleModal={handleModal}
          type="message"
          modalAction={() => removeMessage(deleteId)}
        />
        <div className="flex-1 marginRight-15">
          {!scheduleMessageID && (
            <FormCard title={p('templatesTitle')} className="mb-3">
              <p className="msg-templates__hint">{p('templatesHint')}</p>
              <div className="msg-templates">
                {templates.map((template) => {
                  const Icon = template.icon;
                  const unavailable = template.noDate || template.tooLate;
                  return (
                    <button
                      key={template.key}
                      type="button"
                      className="msg-template"
                      disabled={unavailable}
                      onClick={() => applyTemplate(template)}
                    >
                      <span className="msg-template__icon">
                        <Icon size={18} />
                      </span>
                      <span className="msg-template__text">
                        <span className="msg-template__name">
                          {p(`template-${template.key}`)}
                        </span>
                        <span className="msg-template__when">
                          {templateWhen(template)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </FormCard>
          )}
          <Field
            name="type"
            component={ChoiceCards}
            ariaLabel={t('shared.type')}
            options={[
              {
                value: 'push',
                icon: BellRingOutlineIcon,
                label: p('pushMessage'),
                hint: t('MessagesForm.pushMessageHint'),
              },
              {
                value: 'sms',
                icon: MessageTextOutlineIcon,
                label: 'SMS',
                hint: t('MessagesForm.smsHint'),
              },
              {
                value: 'email',
                icon: EmailOutlineIcon,
                label: 'Email',
                hint: t('MessagesForm.emailHint'),
              },
            ]}
          />

          <FormCard title={t('shared.content')} className="mb-3">
            <div className="form-grid">
              {/* An SMS has no title, so the field is hidden for SMS */}
              {!isSms && (
                <div className="form__form-group form-grid__full">
                  <span className="form-header">{t('shared.title')}</span>
                  <Field
                    name="title"
                    placeholder={`${t('shared.title')} (${t(
                      'EventsContactForm.appNameIfEmpty'
                    )})`}
                    component={renderField}
                    emoji
                  />
                </div>
              )}
              <div className="form__form-group form-grid__full">
                <span className="form-header">{t('shared.message')}</span>
                <Field
                  name="content"
                  placeholder={p('messageContent')}
                  component={renderTextAreaField}
                  emoji
                />
              </div>
            </div>
          </FormCard>

          <FormCard title={p('sendTo')} className="mb-3">
            <Field
              name="receiverType"
              component={ChoiceCards}
              ariaLabel={p('sendTo')}
              options={[
                { value: 'PARTICIPANTS', label: p('participants') },
                { value: 'PRIVILAGED', label: p('invited') },
                { value: 'ALL', label: p('participantsNInvited') },
              ]}
            />
          </FormCard>

          <FormCard
            title={t('ScheduledMessagesPage.timeToSend')}
            className="mb-3"
          >
            <Field
              name="timeToSend"
              component={ChoiceCards}
              ariaLabel={t('ScheduledMessagesPage.timeToSend')}
              options={[
                { value: 'INSTANT', label: t('shared.instant') },
                { value: 'SCHEDULED', label: t('shared.scheduled') },
              ]}
            />
            {formValues.timeToSend === 'SCHEDULED' && (
              <div className="form-grid">
                {/* Above the fields below so the calendar isn't covered */}
                <div className="form__form-group" style={{ zIndex: 100 }}>
                  <span className="form-header">
                    {t('ScheduledMessagesPage.scheduleTime')}
                  </span>
                  <div className="form__form-group-field">
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => {
                        setStartDate(date);
                        change('scheduleTime', date);
                      }}
                      locale="pt-BR"
                      showTimeSelect
                      timeFormat="p"
                      timeIntervals={15}
                      dateFormat="Pp"
                      minDate={new Date()}
                      filterTime={filterPassedTime}
                      placeholderText="DD / MM / YYYY  -- : --"
                      disabledKeyboardNavigation
                    />
                    <div className="form__form-group-icon">
                      <TimetableIcon />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </FormCard>

          {renderSubmitButtons()}
        </div>

        <div className="flex-1 marginLeft-8">
          {formValues.type !== 'email' && (
            <FormCard title={t('NewsForm.appPreview')} className="mb-3">
              <PreviewMessages
                formValues={{
                  heading: formValues?.title,
                  message: formValues?.content,
                  media: formValues?.type,
                }}
              />
            </FormCard>
          )}
          <div className="flex align-items-center p-3 border">
            <div className="modal-icon m-2">
              <AlertCircleOutlineIcon size="38" />
            </div>

            <div>
              <p>{p('pushMessageGroups')}</p>
              <p>{m('AppStorePushGuidelines')}</p>
            </div>
          </div>
        </div>
      </Container>
    </form>
  );
};

EventsContactForm.propTypes = {
  ...reduxFormProps,
  handleSubmit: PropTypes.func.isRequired,
  event: PropTypes.shape({}),
  formValues: PropTypes.shape({
    type: PropTypes.string,
  }),
  // checkCount: PropTypes.func.isRequired,
};

EventsContactForm.defaultProps = {
  event: null,
  formValues: {},
};

const reduxF = reduxForm({
  form: 'EventsContactForm',
  validate: validateEventsContact,
});

const mapStateToProps = (state) => ({
  formValues: getFormValues('EventsContactForm')(state),
});

export default reduxF(connect(mapStateToProps)(EventsContactForm));
